import { SignUpFormData, User, LoginResponse } from "../domain/types/auth.types";
import { httpService } from "./http.service";
import { API_ENDPOINTS } from "../config/api.config";
import { ProfileService } from "./profile.service";

// Real implementation based on the Postman collection
export class AuthService {
  // Sign Up (Register)
  static async signUp(
    data: SignUpFormData
  ): Promise<{ user: User; requiresVerification: boolean }> {
    // Only send fields that the API expects
    const registrationData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      password: data.password,
      organisationName: data.organisationName,
      // Phone and roles will be added later through account settings
    };

    const response = await httpService.post<{
      user?: User;
      message?: string;
      id?: string;
      email?: string;
    }>(API_ENDPOINTS.auth.register, registrationData);

    // Create user object from response
    const user: User = response.user || {
      id: response.id || "",
      email: response.email || data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: "", // Will be added later
      clinicName: data.organisationName,
      mfaRequired: false, // Default value for new users
      createdAt: new Date(),
    };

    // Save user data for later use during login
    this.saveCurrentUser(user);

    return {
      user,
      requiresVerification: true, // Email verification is required
    };
  }

  // Login
  static async login(
    email: string,
    password: string
  ): Promise<{
    user: User | null;
    authToken: string | null;
    requiresMFA: boolean;
    preAuthToken?: string;
  }> {
    const response = await httpService.post<LoginResponse>(API_ENDPOINTS.auth.login, {
      email,
      password,
    });

    if (response.preAuthToken) {
      // MFA is required, store pre-auth token
      sessionStorage.setItem("preAuthToken", response.preAuthToken);
      return {
        user: null,
        authToken: null,
        requiresMFA: true,
        preAuthToken: response.preAuthToken,
      };
    }

    if (response.accessToken && response.refreshToken) {
      // No MFA required, login successful
      // Store both tokens
      this.saveAuthToken(response.accessToken);
      this.saveRefreshToken(response.refreshToken);
      httpService.setAuthToken(response.accessToken);

      localStorage.removeItem("preAuthToken");
      sessionStorage.removeItem("preAuthToken");

      // Get user ID from JWT token
      const userId = this.getUserIdFromToken(response.accessToken);

      // Get existing user data from storage (from signup)
      const existingUser = this.getCurrentUser();
      const user: User = {
        id: userId || existingUser?.id || "",
        email: email,
        firstName: existingUser?.firstName || "",
        lastName: existingUser?.lastName || "",
        phone: existingUser?.phone,
        clinicName: existingUser?.clinicName,
        mfaRequired: response.mfaRequired || false,
        createdAt: existingUser?.createdAt || new Date(),
      };

      this.saveCurrentUser(user);

      // Fetch and save user profile after successful login
      if (userId) {
        try {
          const profile = await ProfileService.getUserProfile(userId);
          const updatedUser = {
            ...user,
            firstName: profile.firstName,
            lastName: profile.lastName,
            phone: profile.phone,
            mfaRequired: profile.mfaRequired,
            organisationName: profile.organisationName,
            companyRole: profile.companyRole,
            username: profile.username,
            roles: profile.roles,
          };
          this.saveCurrentUser(updatedUser);
          ProfileService.saveUserProfile(profile);
        } catch (error) {
          console.warn("Failed to fetch user profile after login:", error);
        }
      }

      return {
        user,
        authToken: response.accessToken,
        requiresMFA: false,
      };
    }

    return {
      user: null,
      authToken: null,
      requiresMFA: response.mfaRequired || false,
    };
  }

  // Refresh Access Token
  static async refreshAccessToken(): Promise<{ accessToken: string; refreshToken: string } | null> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await httpService.post<LoginResponse>(API_ENDPOINTS.auth.refresh, {
        refreshToken,
      });

      if (response.accessToken && response.refreshToken) {
        this.saveAuthToken(response.accessToken);
        this.saveRefreshToken(response.refreshToken);
        httpService.setAuthToken(response.accessToken);
        return {
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        };
      }
      return null;
    } catch (error) {
      // Refresh token is invalid or expired, clear all auth data
      this.clearAuthToken();
      this.clearRefreshToken();
      this.clearCurrentUser();
      return null;
    }
  }

  // Logout
  static async logout(): Promise<void> {
    const refreshToken = this.getRefreshToken();

    if (refreshToken) {
      try {
        await httpService.post(API_ENDPOINTS.auth.logout, { refreshToken });
      } catch (error) {
        console.warn("Logout request failed:", error);
      }
    }

    // Clear all local data
    this.clearAuthToken();
    this.clearRefreshToken();
    this.clearCurrentUser();
    httpService.clearAuthToken();
    localStorage.removeItem("userProfile");
    localStorage.removeItem("organisationData");
    sessionStorage.clear();
  }

  // Verify MFA
  static async verifyMFA(
    authenticatorCode?: string,
    emailCode?: string,
    phoneCode?: string
  ): Promise<boolean> {
    const preAuthToken = sessionStorage.getItem("preAuthToken");
    if (!preAuthToken) {
      throw new Error("Pre-authentication token not found");
    }

    const response = await httpService.post<LoginResponse>(
      API_ENDPOINTS.auth.verifyMFA,
      {
        authenticatorCode,
        emailCode,
        phoneCode,
      },
      {
        headers: {
          Authorization: `Bearer ${preAuthToken}`,
        },
      }
    );

    if (response.accessToken && response.refreshToken) {
      // Store both tokens
      this.saveAuthToken(response.accessToken);
      this.saveRefreshToken(response.refreshToken);
      httpService.setAuthToken(response.accessToken);

      sessionStorage.removeItem("preAuthToken");
      localStorage.removeItem("preAuthToken");

      // Get user ID from JWT token
      const userId = this.getUserIdFromToken(response.accessToken);

      // Get existing user data from storage
      const existingUser = this.getCurrentUser();

      const user: User = {
        id: userId || existingUser?.id || "",
        email: existingUser?.email || "",
        firstName: existingUser?.firstName || "",
        lastName: existingUser?.lastName || "",
        phone: existingUser?.phone,
        clinicName: existingUser?.clinicName,
        mfaRequired: response.mfaRequired || false,
        createdAt: existingUser?.createdAt || new Date(),
      };

      this.saveCurrentUser(user);

      // Fetch and save user profile after successful MFA verification
      if (userId) {
        try {
          const profile = await ProfileService.getUserProfile(userId);
          const updatedUser = {
            ...user,
            firstName: profile.firstName,
            lastName: profile.lastName,
            phone: profile.phone,
            clinicName: profile.clinicName,
            mfaRequired: profile.mfaRequired,
            organisationName: profile.organisationName,
            companyRole: profile.companyRole,
            username: profile.username,
            roles: profile.roles,
          };
          this.saveCurrentUser(updatedUser);
          ProfileService.saveUserProfile(profile);
        } catch (error) {
          console.warn("Failed to fetch user profile after MFA verification:", error);
        }
      }

      return true;
    }

    return false;
  }

  // Activate Account (for invited users)
  static async activateAccount(
    token: string,
    firstName: string,
    lastName: string,
    password: string
  ): Promise<{
    user: User;
    authToken: string;
  }> {
    const response = await httpService.post<{
      id: string;
      username: string;
      email: string;
      firstName: string;
      lastName: string;
      phoneNumber: string | null;
      status: string;
      emailVerified: boolean;
      phoneVerified: boolean;
      mfaEnabled: boolean;
      invitationToken: string | null;
      invitationTokenExpiresAt: string | null;
      createdAt: string;
      updatedAt: string;
      roles: string[];
    }>(API_ENDPOINTS.users.activate, {
      token,
      firstName,
      lastName,
      password,
    });

    // Now automatically log the user in
    const loginResult = await this.login(response.email, password);

    if (!loginResult.authToken || !loginResult.user) {
      throw new Error("Failed to log in after activation");
    }

    return {
      user: loginResult.user,
      authToken: loginResult.authToken,
    };
  }

  // Email Verification (after registration)
  static async verifyEmail(token: string): Promise<{
    success: boolean;
    user?: User;
    authToken?: string | null;
  }> {
    const response = await httpService.get<{ success?: boolean; message?: string }>(
      `${API_ENDPOINTS.verify.emailConfirm}?token=${token}`
    );

    // Backend returns {message: "Email verified successfully."} on success
    const isSuccess = response.success === true || !!response.message;

    if (isSuccess) {
      // Try to get stored credentials from signup
      const storedCredentials = localStorage.getItem("signupCredentials");
      if (storedCredentials) {
        try {
          const { email, password } = JSON.parse(storedCredentials);

          // Automatically login the user
          const loginResult = await this.login(email, password);

          // Clear stored credentials after successful login
          localStorage.removeItem("signupCredentials");

          return {
            success: true,
            user: loginResult.user || undefined,
            authToken: loginResult.authToken,
          };
        } catch (loginError) {
          // If login fails, still return successful verification
          localStorage.removeItem("signupCredentials");
          console.warn("Email verified but auto-login failed:", loginError);
        }
      }
    }

    return { success: isSuccess };
  }

  // Resend Email Verification
  static async resendEmailVerification(): Promise<void> {
    await httpService.post(API_ENDPOINTS.account.emailResend);
  }

  // Phone Verification - Initiate
  static async initiatePhoneVerification(phoneNumber: string): Promise<void> {
    await httpService.post(API_ENDPOINTS.account.phoneInitiate, { phoneNumber });
  }

  // Phone Verification - Confirm
  static async confirmPhoneVerification(otp: string): Promise<boolean> {
    const response = await httpService.post<{ success: boolean }>(
      API_ENDPOINTS.account.phoneConfirm,
      { otp }
    );
    return response.success;
  }

  // Password Reset - Request
  static async requestPasswordReset(email: string): Promise<void> {
    await httpService.post(API_ENDPOINTS.password.forgot, { email });
    sessionStorage.setItem("resetEmail", email);
  }

  // Password Reset - Submit new password
  static async resetPassword(token: string, newPassword: string): Promise<void> {
    await httpService.post(API_ENDPOINTS.password.reset, {
      token,
      newPassword,
    });
    sessionStorage.removeItem("resetEmail");
  }

  // MFA Setup - Initiate
  static async initiateMFA(): Promise<{ qrCode?: string; secret?: string }> {
    const response = await httpService.post<{
      qrCodeUrl?: string;
      manualSecret?: string;
    }>(API_ENDPOINTS.account.mfaInitiate);

    // Map API response to expected format
    return {
      qrCode: response.qrCodeUrl,
      secret: response.manualSecret,
    };
  }

  // MFA Setup - Verify
  static async verifyMFASetup(code: string): Promise<boolean> {
    const response = await httpService.post<{ success: boolean }>(API_ENDPOINTS.account.mfaVerify, {
      code,
    });
    return response.success;
  }

  // Get current user from local storage only (system endpoint no longer supported)
  static async fetchCurrentUser(): Promise<User | null> {
    // Since system endpoints are not supported, we can only return stored user data
    // User info should be obtained via profiles API using userId from login/MFA responses
    return this.getCurrentUser();
  }

  // User Management (Local Storage)
  static getCurrentUser(): User | null {
    const userStr = localStorage.getItem("currentUser");
    return userStr ? JSON.parse(userStr) : null;
  }

  static saveCurrentUser(user: User): void {
    localStorage.setItem("currentUser", JSON.stringify(user));
  }

  static clearCurrentUser(): void {
    localStorage.removeItem("currentUser");
    this.clearAuthToken();
    this.clearRefreshToken();
    httpService.clearAuthToken();
  }

  // Auth Token Management (Local Storage)
  static getAuthToken(): string | null {
    return localStorage.getItem("authToken");
  }

  static saveAuthToken(token: string): void {
    localStorage.setItem("authToken", token);
  }

  static clearAuthToken(): void {
    localStorage.removeItem("authToken");
  }

  // Refresh Token Management (Local Storage)
  static getRefreshToken(): string | null {
    return localStorage.getItem("refreshToken");
  }

  static saveRefreshToken(token: string): void {
    localStorage.setItem("refreshToken", token);
  }

  static clearRefreshToken(): void {
    localStorage.removeItem("refreshToken");
  }

  // Decode JWT token to extract user ID
  static getUserIdFromToken(token: string): string | null {
    try {
      const payload = token.split(".")[1];
      const decoded = JSON.parse(atob(payload));
      return decoded.user_id || decoded.sub || null;
    } catch (error) {
      console.error("Failed to decode JWT token:", error);
      return null;
    }
  }

  // Helper methods for backward compatibility
  static async verifyPhone(phone: string, code: string): Promise<boolean> {
    // This now uses the confirmPhoneVerification method
    return this.confirmPhoneVerification(code);
  }

  static async resendCode(type: "email" | "phone"): Promise<void> {
    if (type === "email") {
      await this.resendEmailVerification();
    } else {
      // For phone, you might need to re-initiate the phone verification
      const user = this.getCurrentUser();
      if (user?.phone) {
        await this.initiatePhoneVerification(user.phone);
      }
    }
  }

  static async sendMFACode(method: "sms" | "email"): Promise<void> {
    const preAuthToken = sessionStorage.getItem("preAuthToken");
    if (!preAuthToken) {
      throw new Error("Pre-authentication token not found");
    }

    const endpoint =
      method === "email" ? API_ENDPOINTS.auth.resendEmail : API_ENDPOINTS.auth.resendPhone;

    await httpService.post<{ message: string }>(
      endpoint,
      {},
      {
        headers: {
          Authorization: `Bearer ${preAuthToken}`,
        },
      }
    );
  }

  static async verifyResetCode(code: string): Promise<boolean> {
    // The reset flow seems to use a token, not a code verification
    // Store the code as token for the reset password call
    sessionStorage.setItem("resetToken", code);
    return true;
  }

  static async resendResetCode(): Promise<void> {
    const email = sessionStorage.getItem("resetEmail");
    if (email) {
      await this.requestPasswordReset(email);
    }
  }

  static completeMFALogin(): User | null {
    return this.getCurrentUser();
  }
}
